// Blog posts data source.
// Each entry is rendered by the Blog index and the BlogPost page.
// Content is an array of typed blocks so new articles stay easy to author.

// Per-article branding. Each security assessment is themed with the audited
// project's own brand color and typeface (scoped to that article only, it does
// not affect the rest of the site). Accent colors are taken from each project's
// official branding; the cover gradient and surfaces are derived from the accent.

const canopyTheme = {
  name: "canopy",
  accent: "#4BB14B",
  accentSoft: "#7BD37B",
  bg: "#0A0F0B",
  surface: "rgba(75, 177, 75, 0.07)",
  border: "rgba(75, 177, 75, 0.28)",
  font: "'Space Grotesk', ui-sans-serif, system-ui, sans-serif",
  fontHref:
    "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap",
  logo: "/assets/icons/canopy-logo.svg",
  coverGradient:
    "radial-gradient(120% 120% at 0% 0%, rgba(75,177,75,0.40) 0%, rgba(10,15,11,0) 55%), linear-gradient(135deg, #0A0F0B 0%, #0F1A10 55%, #123016 100%)",
};

const dydxTheme = {
  name: "dydx",
  accent: "#6966FF",
  accentSoft: "#B4ADFF",
  bg: "#0A0A14",
  surface: "rgba(105, 102, 255, 0.07)",
  border: "rgba(105, 102, 255, 0.28)",
  font: "'Inter', ui-sans-serif, system-ui, sans-serif",
  fontHref:
    "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap",
  logo: "/assets/icons/dydx-logo.svg",
  coverGradient:
    "radial-gradient(120% 120% at 0% 0%, #6966FF40 0%, #0A0A1400 55%), linear-gradient(135deg, #0A0A14 0%, #1B1B3E 55%, #323177 100%)",
};

const dypiusTheme = {
  name: "dypius",
  accent: "#1496DC",
  accentSoft: "#85CDF3",
  bg: "#06101C",
  surface: "rgba(20, 150, 220, 0.07)",
  border: "rgba(20, 150, 220, 0.28)",
  font: "'Poppins', ui-sans-serif, system-ui, sans-serif",
  fontHref:
    "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap",
  logo: "/assets/icons/dypius-logo.png",
  coverGradient:
    "radial-gradient(120% 120% at 0% 0%, #1496DC40 0%, #06101C00 55%), linear-gradient(135deg, #06101C 0%, #09283F 55%, #0C486D 100%)",
};

const infinifiTheme = {
  name: "infinifi",
  accent: "#1A7271",
  accentSoft: "#72C9B6",
  bg: "#07130F",
  surface: "rgba(26, 114, 113, 0.07)",
  border: "rgba(26, 114, 113, 0.28)",
  font: "'Space Grotesk', ui-sans-serif, system-ui, sans-serif",
  fontHref:
    "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap",
  logo: "/assets/icons/infinifi-logo.png",
  coverGradient:
    "radial-gradient(120% 120% at 0% 0%, #1A727140 0%, #07130F00 55%), linear-gradient(135deg, #07130F 0%, #0A2421 55%, #0F3B38 100%)",
};

const lavaTheme = {
  name: "lava",
  accent: "#FF3900",
  accentSoft: "#EF6000",
  bg: "#160604",
  surface: "rgba(255, 57, 0, 0.07)",
  border: "rgba(255, 57, 0, 0.28)",
  font: "'Space Grotesk', ui-sans-serif, system-ui, sans-serif",
  fontHref:
    "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap",
  logo: "/assets/icons/lava-logo.png",
  coverGradient:
    "radial-gradient(120% 120% at 0% 0%, #FF390040 0%, #16060400 55%), linear-gradient(135deg, #160604 0%, #400F03 55%, #781B02 100%)",
};

const lineaTheme = {
  name: "linea",
  accent: "#6119EF",
  accentSoft: "#61DFFF",
  bg: "#0B0716",
  surface: "rgba(97, 25, 239, 0.07)",
  border: "rgba(97, 25, 239, 0.28)",
  font: "'Space Grotesk', ui-sans-serif, system-ui, sans-serif",
  fontHref:
    "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap",
  logo: "/assets/icons/linea-logo.png",
  coverGradient:
    "radial-gradient(120% 120% at 0% 0%, #6119EF40 0%, #0B071600 55%), linear-gradient(135deg, #0B0716 0%, #1A0A3D 55%, #2F0F71 100%)",
};

const mantleTheme = {
  name: "mantle",
  accent: "#65B3AE",
  accentSoft: "#D3D5D6",
  bg: "#0A0F0E",
  surface: "rgba(101, 179, 174, 0.07)",
  border: "rgba(101, 179, 174, 0.28)",
  font: "'Mulish', ui-sans-serif, system-ui, sans-serif",
  fontHref:
    "https://fonts.googleapis.com/css2?family=Mulish:wght@400;500;600;700&display=swap",
  logo: "/assets/icons/mantle-logo.png",
  coverGradient:
    "radial-gradient(120% 120% at 0% 0%, #65B3AE40 0%, #0A0F0E00 55%), linear-gradient(135deg, #0A0F0E 0%, #1A2D2B 55%, #305451 100%)",
};

const orochiTheme = {
  name: "orochi",
  accent: "#FF4608",
  accentSoft: "#62B0EA",
  bg: "#160704",
  surface: "rgba(255, 70, 8, 0.07)",
  border: "rgba(255, 70, 8, 0.28)",
  font: "'Raleway', ui-sans-serif, system-ui, sans-serif",
  fontHref:
    "https://fonts.googleapis.com/css2?family=Raleway:wght@400;500;600;700&display=swap",
  logo: "/assets/icons/orochi-logo.svg",
  coverGradient:
    "radial-gradient(120% 120% at 0% 0%, #FF460840 0%, #16070400 55%), linear-gradient(135deg, #160704 0%, #401205 55%, #782106 100%)",
};

const passageTheme = {
  name: "passage",
  accent: "#F28F38",
  accentSoft: "#FFD4AE",
  bg: "#140C04",
  surface: "rgba(242, 143, 56, 0.07)",
  border: "rgba(242, 143, 56, 0.28)",
  font: "'Poppins', ui-sans-serif, system-ui, sans-serif",
  fontHref:
    "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap",
  logo: "/assets/icons/passage-logo.svg",
  coverGradient:
    "radial-gradient(120% 120% at 0% 0%, #F28F3840 0%, #140C0400 55%), linear-gradient(135deg, #140C04 0%, #3C240D 55%, #71431A 100%)",
};

const poktTheme = {
  name: "pokt",
  accent: "#50BBB6",
  accentSoft: "#83ECB8",
  bg: "#06120F",
  surface: "rgba(80, 187, 182, 0.07)",
  border: "rgba(80, 187, 182, 0.28)",
  font: "'Inter', ui-sans-serif, system-ui, sans-serif",
  fontHref:
    "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap",
  logo: "/assets/icons/pokt-logo.png",
  coverGradient:
    "radial-gradient(120% 120% at 0% 0%, #50BBB640 0%, #06120F00 55%), linear-gradient(135deg, #06120F 0%, #13302D 55%, #255955 100%)",
};

const storyTheme = {
  name: "story",
  accent: "#2F6A4C",
  accentSoft: "#F8F8F6",
  bg: "#08110C",
  surface: "rgba(47, 106, 76, 0.07)",
  border: "rgba(47, 106, 76, 0.28)",
  font: "'Inter', ui-sans-serif, system-ui, sans-serif",
  fontHref:
    "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap",
  logo: "/assets/icons/story-logo.png",
  coverGradient:
    "radial-gradient(120% 120% at 0% 0%, #2F6A4C40 0%, #08110C00 55%), linear-gradient(135deg, #08110C 0%, #0F2118 55%, #183627 100%)",
};

const wpoktTheme = {
  name: "wpokt",
  accent: "#50BBB6",
  accentSoft: "#83ECB8",
  bg: "#06120F",
  surface: "rgba(80, 187, 182, 0.07)",
  border: "rgba(80, 187, 182, 0.28)",
  font: "'Inter', ui-sans-serif, system-ui, sans-serif",
  fontHref:
    "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap",
  logo: "/assets/icons/wpokt-logo.png",
  coverGradient:
    "radial-gradient(120% 120% at 0% 0%, #50BBB640 0%, #06120F00 55%), linear-gradient(135deg, #06120F 0%, #13302D 55%, #255955 100%)",
};

// Shared severity card colors so ratings stay semantically consistent across
// every article regardless of the project's brand accent.
const SEV = {
  critical: "#7a65d0",
  high: "#e06c5e",
  medium: "#e2c044",
  low: "#4BB14B",
  info: "#7ec8e3",
};

const PDF_CLOSING =
  "The full report — including detailed findings, system-design trust boundaries, the remediation roadmap, and severity definitions — is available as a PDF below.";

export const posts = [
  {
    slug: "orochi-network-bridge-security-assessment",
    title: "Orochi Network Bridge: Security Assessment",
    date: "June 25, 2026",
    author: "Nodefleet Security",
    readingTime: "4 min read",
    tags: ["Security Assessment", "Bridge", "CCIP", "Orochi Network"],
    theme: orochiTheme,
    pdfUrl:
      "https://github.com/nodefleet/publications/raw/main/OROCHI-NETWORK-ON-BRIDGE-SECURITY-ASSESSMENT.pdf",
    pdfViewUrl:
      "https://github.com/nodefleet/publications/blob/main/OROCHI-NETWORK-ON-BRIDGE-SECURITY-ASSESSMENT.pdf",
    excerpt:
      "Orochi's ON token bridge moves value between Ethereum and BNB Smart Chain using a Chainlink CCT design and a 1:1 WrappedON wrapper. Nodefleet reviewed it — 3 findings, no critical or high issues.",
    severity: [
      { level: "Critical", count: 0, summary: "No critical findings", color: SEV.critical },
      { level: "High", count: 0, summary: "No high-severity findings", color: SEV.high },
      { level: "Medium", count: 2, summary: "Reserve custody via pool rebalancer; rate-limit authority", color: SEV.medium },
      { level: "Low", count: 1, summary: "Mainnet fork tests not enforced by default", color: SEV.low },
      { level: "Info", count: 0, summary: "No informational findings recorded", color: SEV.info },
    ],
    content: [
      { type: "callout", text: "Prepared for the Orochi Network community. Repository under review: github.com/orochi-network/bridge. Engagement date: June 25, 2026." },
      { type: "heading", text: "Executive summary" },
      { type: "paragraph", text: "Orochi Network's bridge moves the ON token between Ethereum and BNB Smart Chain. On BSC the canonical ON token is held by a stock Chainlink LockReleaseTokenPool; on Ethereum a custom WrappedON contract acts as a 1:1 wrapper where anyone can deposit native ON for wON and redeem it when the reserve is sufficient and the contract is not paused." },
      { type: "paragraph", text: "The WrappedON contract is unusually mature for a narrow bridge surface — OpenZeppelin upgradeable ERC-20, AccessControl, Pausable, UUPS, and ERC-7201 namespaced storage, decimals checks, received-amount accounting, transient reentrancy guards, timelocked upgrades, and a CCIP mint cap. The deployment scripts are equally defensive, with idempotent deploys, role-handoff gates, two-step CCIP admin handoff, and rate-limit validation. The assessment identified 3 findings: no critical or high-severity issues, two Medium and one Low." },
      { type: "heading", text: "Key findings" },
      { type: "list", items: [
        "Medium — BSC locked-reserve custody remains controlled through the stock Chainlink pool rebalancer path; after handoff a pool owner can set a rebalancer able to withdraw the reserve liquidity that backs Ethereum-to-BSC releases.",
        "Medium — Rate-limit authority can disable bridge throughput controls.",
        "Low — Mainnet fork coverage exists but is not enforced by the default test run.",
      ] },
      { type: "heading", text: "Conclusion" },
      { type: "paragraph", text: "The residual risk is concentrated in the inherited Chainlink CCT trust model rather than in Orochi's own contract code, which is well-built and defensively deployed. The priority is operational discipline around the BSC reserve pool's privileged roles and making fork-based tests part of the default pipeline." },
      { type: "paragraph", text: PDF_CLOSING },
    ],
  },
  {
    slug: "wcnpy-bridge-to-web3-ai-secured",
    title: "wCNPY: The Bridge to Web3 AI, Secured",
    date: "June 16, 2026",
    author: "Nodefleet Security",
    readingTime: "5 min read",
    tags: ["Security Assessment", "Bridge", "ERC-20", "Canopy"],
    theme: canopyTheme,
    pdfUrl:
      "https://github.com/nodefleet/publications/raw/main/WCNPY-SECURITY-ASSESSMENT.pdf",
    pdfViewUrl:
      "https://github.com/nodefleet/publications/blob/main/WCNPY-SECURITY-ASSESSMENT.pdf",
    excerpt:
      "Canopy is building onchain apps with AI. wCNPY is the wrapped $CNPY bridge token that carries that value onto EVM chains. Nodefleet reviewed the contract — 3 findings, no critical, high, or medium issues.",
    severity: [
      { level: "Critical", count: 0, summary: "No critical findings", color: "#7a65d0" },
      { level: "High", count: 0, summary: "No high-severity findings", color: "#e06c5e" },
      { level: "Medium", count: 0, summary: "No medium-severity findings", color: "#e2c044" },
      { level: "Low", count: 1, summary: "Owner mint authority and off-chain reconciliation", color: "#4BB14B" },
      { level: "Info", count: 2, summary: "Pause semantics and placeholder security contact", color: "#7ec8e3" },
    ],
    content: [
      {
        type: "callout",
        text: "Prepared for Canopy Network Labs. Contract under review: github.com/canopy-network/wCNPY. Engagement date: June 16, 2026.",
      },
      {
        type: "heading",
        text: "Why a bridge token matters for web3 AI",
      },
      {
        type: "paragraph",
        text: "Canopy's mission is to let builders ship onchain apps with AI and launch in minutes. Moving that value across chains needs a dependable bridge, and wCNPY is the ERC-20 token that represents bridged $CNPY in an EVM-compatible environment. Because a bridge token is the settlement boundary between on-chain balances and off-chain accounting, its trust model — not just its code — is what keeps user value safe.",
      },
      {
        type: "heading",
        text: "Executive summary",
      },
      {
        type: "paragraph",
        text: "On June 16, 2026, Nodefleet performed a focused security assessment of the Canopy wCNPY contract. The review concentrated on the contract's effective trust boundaries: owner-controlled minting, holder-initiated redemption, pause behavior, ownership transfer safety, deployment assumptions, and the off-chain bridge flow implied by the RedeemRequested event.",
      },
      {
        type: "paragraph",
        text: "The contract is structurally conservative. It inherits widely used OpenZeppelin components, keeps custom logic small, disables ownership renunciation to avoid permanently bricking bridge administration, and uses two-step ownership transfer to reduce accidental control handoff risk. No critical, high, or medium-severity issues were identified in scope. No authorization bypass, reentrancy path, arithmetic flaw, or direct loss-of-funds vulnerability was observed in the reviewed code.",
      },
      {
        type: "paragraph",
        text: "Three observations were recorded. One low-severity observation concerns the fact that wrapped supply integrity depends entirely on privileged mint authorization and off-chain reconciliation, with no on-chain mint cap, rate limit, or proof requirement. Two informational observations concern pause semantics and incident-response hygiene: pausing halts bridge entry and exit but does not freeze ordinary ERC-20 transfers, and the NatSpec security contact still contains placeholder text rather than a monitored disclosure channel.",
      },
      {
        type: "heading",
        text: "What the contract does",
      },
      {
        type: "list",
        items: [
          "mint(address to, uint256 amount) is restricted to the owner and blocked while paused.",
          "redeem(uint256 amount, address canopyAddress) burns from msg.sender, rejects zero amount and zero destination, and emits RedeemRequested.",
          "renounceOwnership() is intentionally disabled to avoid permanent administrative lockout.",
          "transferOwnership() uses a two-step acceptance flow through Ownable2Step.",
          "The token uses six decimals; standard ERC-20 transfer and approval paths remain active during pause.",
        ],
      },
      {
        type: "heading",
        text: "Positive security properties",
      },
      {
        type: "list",
        items: [
          "No upgradeability or proxy administration layer.",
          "No external calls in mint() or redeem().",
          "No custom arithmetic or complex mutable accounting beyond total supply and balances.",
          "Clear custom errors for zero amount, zero Canopy address, and disabled renounce.",
          "Explicit unit tests for the most security-relevant code paths (16 Foundry tests passing).",
          "Inherited deployment-time protection against zero-address ownership.",
        ],
      },
      {
        type: "heading",
        text: "Findings",
      },
      {
        type: "list",
        items: [
          "Low — Wrapped supply integrity depends on owner mint authority and off-chain reconciliation; there is no on-chain mint cap, rate limit, or proof requirement.",
          "Info — Pause does not suspend ERC-20 transfers or approvals; it halts only bridge entry (mint) and exit (redeem), so \"pause\" is not a full token freeze.",
          "Info — The security disclosure contact remains placeholder text rather than a monitored channel.",
        ],
      },
      {
        type: "heading",
        text: "Conclusion",
      },
      {
        type: "paragraph",
        text: "The reviewed code is consistent with a bridge-token design that intentionally relies on trusted administration, provided the project treats owner key management, mint/redeem reconciliation, and relayer operations as first-class security controls. The most important residual risk is not a Solidity exploit path — it is the bridge's privileged trust model. If those controls are sound, the contract is straightforward and dependable. The residual risk is primarily operational and should be addressed through deployment discipline rather than major contract changes.",
      },
      {
        type: "paragraph",
        text: "The full report — including detailed findings, bridge trust boundaries, the remediation roadmap, and severity definitions — is available as a PDF below.",
      },
    ],
  },
  {
    slug: "canopy-network-security-assessment",
    title: "Canopy Network Core Client (Go): Application Security Assessment",
    date: "June 1, 2026",
    author: "Nodefleet Security",
    readingTime: "6 min read",
    tags: ["Security Assessment", "Consensus", "Blockchain", "Canopy"],
    theme: canopyTheme,
    pdfUrl:
      "https://github.com/nodefleet/publications/raw/main/CANOPY-NETWORK-SECURITY-ASSESSMENT.pdf",
    pdfViewUrl:
      "https://github.com/nodefleet/publications/blob/main/CANOPY-NETWORK-SECURITY-ASSESSMENT.pdf",
    excerpt:
      "Nodefleet reviewed the security and resilience of the Canopy Network consensus engine and validator-facing components. The engagement identified 15 findings in scope, no critical issues, and concrete, targeted remediation work.",
    // Severity breakdown rendered as branded cards on the article page.
    severity: [
      { level: "Critical", count: 0, summary: "No critical findings.", color: "#7a65d0" },
      { level: "High", count: 1, summary: "File permissions", color: "#e06c5e" },
      { level: "Medium", count: 6, summary: "BFT, slashing, P2P, IPC, and recovery issues", color: "#e2c044" },
      { level: "Low", count: 8, summary: "P2P reputation, plugin resilience, and overlay", color: "#4BB14B" },
      { level: "Info", count: 0, summary: "No informational findings recorded", color: "#7ec8e3" },
    ],
    content: [
      {
        type: "callout",
        text: "Prepared for Canopy Network Labs. Repository under review: github.com/canopy-network/canopy. Engagement date: June 1, 2026.",
      },
      {
        type: "heading",
        text: "Executive summary",
      },
      {
        type: "paragraph",
        text: "Canopy engaged Nodefleet to review the security and resilience of its consensus engine and related validator-facing components. The review focused on the client-provided source code for the BFT, FSM, P2P, store, library, and consensus-adjacent controller layers.",
      },
      {
        type: "paragraph",
        text: "The Canopy codebase contains several strong architectural foundations, including explicit consensus phase transitions, centralized quorum-certificate validation, a clear lock concept, modular controller/FSM separation, authenticated peer transport, and identifiable persistence boundaries. These design elements provide a structured basis for protocol review and targeted remediation.",
      },
      {
        type: "paragraph",
        text: "The assessment identified 15 findings in scope. No critical issues were recorded. The most significant issue concerned overly permissive filesystem modes for private keys, keystores, configuration files, and other persisted control artifacts. Because these files may hold validator signing material or operational configuration, restrictive file-permission defaults should be treated as a high-priority remediation item before production or institutional validator deployments.",
      },
      {
        type: "paragraph",
        text: "The remaining findings were concentrated in consensus-adjacent validation, validator networking, plugin IPC reliability, slashing accountability, and persistence robustness. Overall, the findings do not suggest the system requires a fundamental redesign. Instead, they identify concrete implementation and operational improvements that can be addressed through targeted engineering work.",
      },
      {
        type: "heading",
        text: "Scope",
      },
      {
        type: "paragraph",
        text: "The review was a time-boxed, evidence-based, source-code security assessment of the repository's core protocol surfaces and consensus-adjacent components. Packages reviewed included:",
      },
      {
        type: "list",
        items: [
          "bft/ — consensus implementation, message handling, voting, evidence handling, and leader election",
          "fsm/ — state transition logic, transactions, governance, byzantine evidence, committees, and validators",
          "p2p/ — peer-to-peer transport, connection handling, encryption, peer book, and abuse-response hooks",
          "store/ — versioned storage, transactions, sparse Merkle tree logic, indexing, and proof APIs",
          "controller/ — block, consensus, controller, and transaction coordination logic",
          "lib/ — consensus certificates, plugin integration, configuration, storage helpers, and key management",
        ],
      },
      {
        type: "heading",
        text: "Methodology",
      },
      {
        type: "paragraph",
        text: "Nodefleet combined manual review of the BFT engine and its direct call graph with an invariant review of the consensus and persistence guarantees, plus test and build validation. Core invariants checked included: a replica should only unlock when it receives a valid higher-round lock certificate or observes the same payload it previously locked on; any field that affects consensus control flow should be cryptographically authenticated; blocks should not be committed unless the associated quorum certificate is fully validated; and failed store commits should not advance in-memory height beyond the last durable state.",
      },
      {
        type: "paragraph",
        text: "The repository's existing unit tests for the reviewed areas were run and all passed at the audited commit. That result does not reduce the severity of the findings — several are coverage gaps rather than regressions against existing tests.",
      },
      {
        type: "heading",
        text: "Findings overview",
      },
      {
        type: "paragraph",
        text: "Representative findings across the engagement included:",
      },
      {
        type: "list",
        items: [
          "Private keys and control files are persisted with overly permissive filesystem modes (High).",
          "Proposer signatures omit RcBuildHeight and Timestamp.",
          "Safe-node payload matching uses && instead of ||.",
          "Non-signer settlement clears pending evidence for other committees in the same window.",
          "Topic inbox overflow drains queued messages, enabling authenticated liveness degradation.",
          "Concurrent plugin IPC writes are unsynchronized and may corrupt.",
          "Failed commit attempts advance in-memory store height before persistence succeeds.",
        ],
      },
      {
        type: "heading",
        text: "Conclusion",
      },
      {
        type: "paragraph",
        text: "The open issues are concentrated in deterministic validation, governance integrity, authenticated network resource enforcement, validator runtime hardening, and persistence safety. They are relevant to validators, exchanges, indexers, and ecosystem integrators because they may affect validator reliability, local key safety, node recovery behavior, message delivery, and plugin-backed execution. Each can be addressed through focused engineering work rather than architectural change.",
      },
      {
        type: "paragraph",
        text: "The full report — including detailed findings, system-design trust boundaries, the remediation roadmap, and severity definitions — is available as a PDF below.",
      },
    ],
  },
  {
    slug: "linea-zkevm-security-assessment",
    title: "Linea zkEVM (Layer 2 Stack): Security Assessment",
    date: "February 20, 2026",
    author: "Nodefleet Security",
    readingTime: "5 min read",
    tags: ["Security Assessment", "zkEVM", "Layer 2", "Linea"],
    theme: lineaTheme,
    pdfUrl:
      "https://github.com/nodefleet/publications/raw/main/LINEA-ZKEVM-SECURITY-ASSESSMENT.pdf",
    pdfViewUrl:
      "https://github.com/nodefleet/publications/blob/main/LINEA-ZKEVM-SECURITY-ASSESSMENT.pdf",
    excerpt:
      "Linea is a Consensys zkEVM rollup. Nodefleet reviewed the Lineth monorepo — coordinator, rollup contracts, bridge, and sequencer plugin. 5 findings, no critical or high issues; the risks are liveness and accountability, not asset theft.",
    severity: [
      { level: "Critical", count: 0, summary: "No critical findings", color: SEV.critical },
      { level: "High", count: 0, summary: "No high-severity findings", color: SEV.high },
      { level: "Medium", count: 3, summary: "Forced-tx deadlines, V8 finalization, unbound L2 offsets", color: SEV.medium },
      { level: "Low", count: 2, summary: "Postman fee truncation and zero-role pause mappings", color: SEV.low },
      { level: "Info", count: 0, summary: "No informational findings recorded", color: SEV.info },
    ],
    content: [
      { type: "callout", text: "Prepared for the Linea zkEVM community. Repository under review: github.com/LFDT-Lineth/lineth-monorepo (Lineth Monorepo). Engagement date: February 20, 2026." },
      { type: "heading", text: "Executive summary" },
      { type: "paragraph", text: "Linea is a zkEVM Layer 2 developed by Consensys. The review covered the rollup finalization and proof-aggregation path, the coordinator that persists batches, blobs, and aggregations, the canonical message service and token-bridge flows, and the Besu sequencer plugin (transaction-pool validation, selection guards, trace-limit and profitability checks, denylist controls, and a forced-transaction pool)." },
      { type: "paragraph", text: "The assessment identified 5 findings. No critical or high-severity issues were recorded; three are Medium and two are Low. The highest-risk issues are liveness and accountability risks rather than direct asset-theft paths." },
      { type: "heading", text: "Key findings" },
      { type: "list", items: [
        "Medium — Forced transaction deadlines are parsed and stored but never enforced during selection or status recording, so one stale forced transaction can become a queue-level liveness problem.",
        "Medium — Coordinator finalization is not implemented for LineaRollupContractVersion.V8 while other components already treat V8 as supported or required.",
        "Medium — L2 messaging block offsets emitted during finalization are not bound to the proof public input, letting a faulty or privileged finalizer emit misleading anchoring hints without invalidating the proof.",
        "Low — L1 postman queue filtering can permanently strand low-fee messages whose fee-to-gas threshold truncates to zero.",
        "Low — Non-security-council pause role mappings can be set to the zero role.",
      ] },
      { type: "heading", text: "Conclusion" },
      { type: "paragraph", text: "The findings do not indicate a need to redesign the rollup proof model. They point to defensive checks that should live closer to the component that relies on them: enforce forced-transaction deadlines in the sequencer pool, fail closed on contract-version branching before upgrades, proof-bind or document bridge event data, and preserve low-fee messages for later reevaluation instead of truncating them away." },
      { type: "paragraph", text: PDF_CLOSING },
    ],
  },
  {
    slug: "dypius-bsc-token-bridge-security-assessment",
    title: "Dypius BSC Token Bridge: Security Assessment",
    date: "February 15, 2026",
    author: "Nodefleet Security",
    readingTime: "4 min read",
    tags: ["Security Assessment", "Bridge", "ERC-20", "Dypius"],
    theme: dypiusTheme,
    pdfUrl:
      "https://github.com/nodefleet/publications/raw/main/DYP-FINANCE-CONTRACT-SECURITY-ASSESSMENT.pdf",
    pdfViewUrl:
      "https://github.com/nodefleet/publications/blob/main/DYP-FINANCE-CONTRACT-SECURITY-ASSESSMENT.pdf",
    excerpt:
      "Dypius bridges its DYP token between Ethereum and BNB Smart Chain. Nodefleet reviewed the bridge and token contracts — 5 findings, no critical issues, but two High-severity issues in withdrawal authorization.",
    severity: [
      { level: "Critical", count: 0, summary: "No critical findings", color: SEV.critical },
      { level: "High", count: 2, summary: "Zero verifier address; single mutable verifier", color: SEV.high },
      { level: "Medium", count: 3, summary: "Signature scope, caller ban, governance checkpoints", color: SEV.medium },
      { level: "Low", count: 0, summary: "No low-severity findings", color: SEV.low },
      { level: "Info", count: 0, summary: "No informational findings recorded", color: SEV.info },
    ],
    content: [
      { type: "callout", text: "Prepared for the DYP Finance community. Repository under review: github.com/dypfinance/Dypius-token-bridge-bsc. Engagement date: February 15, 2026." },
      { type: "heading", text: "Executive summary" },
      { type: "paragraph", text: "The Dypius Token Bridge BSC repository contains three Solidity contracts: a bridge that accepts DYP deposits, burns bridged tokens, verifies off-chain withdrawal signatures, and mints/transfers DYP to users; the BSC mint/burn DYP token with a max supply and owner-only mint and burn; and the Ethereum DYP ERC-20 with Compound-style vote delegation. The bridge uses SafeERC20 and ReentrancyGuard, binds withdrawal signatures to recipient, amount, chain ID, deposit ID, and contract, records claimed IDs before minting, and applies a per-account daily limit." },
      { type: "paragraph", text: "The assessment identified 5 findings. No critical findings were recorded; two are High and three are Medium. Both High-severity issues affect bridge withdrawal authorization." },
      { type: "heading", text: "Key findings" },
      { type: "list", items: [
        "High — setVerifyAddress accepts the zero address; if the verifier is address(0), invalid ECDSA recovery returning address(0) makes the signature check pass and allows unauthorized withdrawals up to the per-account limit.",
        "High — All withdrawals depend on one mutable verifier address; a compromised verifier can authorize arbitrary withdrawals, bounded only per recipient per day, with no signer threshold, expiry, aggregate cap, or challenge period.",
        "Medium — Withdrawal signatures omit source-chain domain fields and expiry.",
        "Medium — The contract-caller ban weakens administration and wallet compatibility.",
        "Medium — Governance vote checkpoints do not move with token balances.",
      ] },
      { type: "heading", text: "Conclusion" },
      { type: "paragraph", text: "The bridge design is compact and uses solid building blocks, but its security rests almost entirely on a single off-chain signer. The priority remediations are rejecting the zero verifier address, moving to a signer threshold (or multisig) with signature expiry and source-chain binding, and aligning governance checkpoints with balance changes before relying on the bridge for production value transfer." },
      { type: "paragraph", text: PDF_CLOSING },
    ],
  },
  {
    slug: "mantle-network-security-assessment",
    title: "Mantle Network (Layer 2 Stack): Security Assessment",
    date: "February 9, 2026",
    author: "Nodefleet Security",
    readingTime: "5 min read",
    tags: ["Security Assessment", "Layer 2", "Rollup", "Mantle"],
    theme: mantleTheme,
    pdfUrl:
      "https://github.com/nodefleet/publications/raw/main/MANTLE-PROTOCOL-SECURITY-ASSESSMENT.pdf",
    pdfViewUrl:
      "https://github.com/nodefleet/publications/blob/main/MANTLE-PROTOCOL-SECURITY-ASSESSMENT.pdf",
    excerpt:
      "Mantle's pre-Bedrock optimistic rollup stack — rollup contracts, bridges, TSS state-batch signing, EigenDA, and batch services. Nodefleet found 7 findings, no critical, two High affecting withdrawal safety.",
    severity: [
      { level: "Critical", count: 0, summary: "No critical findings", color: SEV.critical },
      { level: "High", count: 2, summary: "TSS batch signing; immediate fraud-proof confirmation", color: SEV.high },
      { level: "Medium", count: 4, summary: "Messaging offsets, rollback fields, deposit crediting, EigenDA proof", color: SEV.medium },
      { level: "Low", count: 1, summary: "Gas-oracle event skipping", color: SEV.low },
      { level: "Info", count: 0, summary: "No informational findings recorded", color: SEV.info },
    ],
    content: [
      { type: "callout", text: "Prepared for the Mantle Network community. Repository under review: github.com/mantlenetworkio/mantle (release v0.4.3-fastsync). Engagement date: February 9, 2026." },
      { type: "heading", text: "Executive summary" },
      { type: "paragraph", text: "Mantle's repository implements its pre-Bedrock optimistic rollup: L1 rollup contracts derived from early Optimism, L1 and L2 bridges, a Canonical Transaction Chain, a State Commitment Chain, Mantle-specific TSS signatures over state batches, EigenDA batch storage and challenge components, an L2 geth fork, batch-submitter services, a gas oracle, and a data transport layer. L2-to-L1 messages are relayed only after a state-root inclusion proof, and the State Commitment Chain requires a collateralized proposer plus a TSS group signature." },
      { type: "paragraph", text: "The assessment identified 7 findings. No critical findings were recorded; two are High, four Medium, and one Low. Both High-severity issues affect withdrawal safety." },
      { type: "heading", text: "Key findings" },
      { type: "list", items: [
        "High — TSS nodes approve a state-batch signing request by checking only the final state root, but the signature authorizes the entire state-root array; a compromised proposer path can get attacker-selected intermediate roots signed and later use them to prove L2-to-L1 messages.",
        "High — The fraud-proof Rollup contract confirms newly created assertions immediately inside createAssertion, leaving challenge functions and assertion deadlines effectively inert.",
        "Medium — L2 messaging block offsets emitted during finalization are not bound to the proof public input.",
        "Medium — Rollback signing requests drop rollback-specific fields in the batch submitter client.",
        "Medium — ERC-20 deposits credit the requested amount instead of the amount actually received.",
        "Medium — The EigenDA fraud proof only detects a fixed sentinel string and is caller-whitelisted.",
        "Low — Gas-oracle overhead processing can skip intermediate state-batch events.",
      ] },
      { type: "heading", text: "Conclusion" },
      { type: "paragraph", text: "The most consequential issues are in the parts of the stack that guarantee withdrawal correctness: TSS state-batch verification should validate every root it authorizes, and fraud-proof assertions must remain challengeable for their full window. Addressing the High and Medium findings hardens withdrawal safety and accounting without redesigning the rollup." },
      { type: "paragraph", text: PDF_CLOSING },
    ],
  },
  {
    slug: "infinifi-protocol-security-assessment",
    title: "infiniFi Protocol: Security Assessment",
    date: "February 3, 2026",
    author: "Nodefleet Security",
    readingTime: "4 min read",
    tags: ["Security Assessment", "DeFi", "Solidity", "infiniFi"],
    theme: infinifiTheme,
    pdfUrl:
      "https://github.com/nodefleet/publications/raw/main/INFINIFI-PROTOCOL-SECURITY-ASSESSMENT.pdf",
    pdfViewUrl:
      "https://github.com/nodefleet/publications/blob/main/INFINIFI-PROTOCOL-SECURITY-ASSESSMENT.pdf",
    excerpt:
      "infiniFi is a Solidity DeFi protocol with receipt tokens, yield-sharing, and farm adapters. Nodefleet reviewed it — 5 findings, no critical or high issues; the Medium issues concern fund liveness and accounting availability.",
    severity: [
      { level: "Critical", count: 0, summary: "No critical findings", color: SEV.critical },
      { level: "High", count: 0, summary: "No high-severity findings", color: SEV.high },
      { level: "Medium", count: 3, summary: "Unbounded unwinding; reverting yield distribution; gateway zap minting", color: SEV.medium },
      { level: "Low", count: 2, summary: "Redemption fallback and zero interpolation durations", color: SEV.low },
      { level: "Info", count: 0, summary: "No informational findings recorded", color: SEV.info },
    ],
    content: [
      { type: "callout", text: "Prepared for the infiniFi Protocol community. Repository under review: github.com/InfiniFi-Labs/infinifi-protocol. Engagement date: February 3, 2026." },
      { type: "heading", text: "Executive summary" },
      { type: "paragraph", text: "infiniFi's mint and redeem controllers are role-gated entry points into receipt-token supply changes. Farm adapters report assets through a common interface, most movement between farms goes through controlled rebalancer contracts, the staked-token flow caches the staked receipt balance to resist intra-block manipulation during reward distribution, and the V2 gateway gates direct staking and unstaking through YieldSharingV2." },
      { type: "paragraph", text: "The assessment identified 5 findings. No critical or high-severity issues were recorded; three are Medium and two are Low. The Medium issues concern user fund liveness and protocol accounting availability." },
      { type: "heading", text: "Key findings" },
      { type: "list", items: [
        "Medium — The unwinding module iterates from a position's start epoch to the current epoch even after the configured unwind completes, so long-unclaimed positions can become increasingly expensive and eventually impractical to withdraw.",
        "Medium — Positive-yield distribution can revert when a positive target illiquid ratio routes rewards to the locking module while no locked or unwinding positions exist.",
        "Medium — Gateway zap functions mint against the gateway's full USDC balance after a router call and expose no gateway-level minimum receipt-token output.",
        "Low — The proportional redemption fallback allocates withdrawals using assets() but calls withdraw() on farms that may only release primary-token liquidity, which can make otherwise recoverable states revert.",
        "Low — YieldSharingV2 and JCurveSmoother allow zero interpolation durations, risking division-by-zero reverts during reward smoothing.",
      ] },
      { type: "heading", text: "Conclusion" },
      { type: "paragraph", text: "The remediation work is direct: bound unwinding calculations to a position's terminal epoch or checkpoint finalized state, make locking-reward distribution tolerant of an empty recipient set, mint zaps only from the USDC balance delta created by the current router call, and reject zero interpolation durations. None of the findings require an architectural redesign." },
      { type: "paragraph", text: PDF_CLOSING },
    ],
  },
  {
    slug: "lava-network-security-assessment",
    title: "Lava Network Protocol (v5.6.2): Security Assessment",
    date: "December 18, 2025",
    author: "Nodefleet Security",
    readingTime: "5 min read",
    tags: ["Security Assessment", "Cosmos SDK", "RPC", "Lava Network"],
    theme: lavaTheme,
    pdfUrl:
      "https://github.com/nodefleet/publications/raw/main/LAVA-NETWORK-SECURITY-ASSESSMENT.pdf",
    pdfViewUrl:
      "https://github.com/nodefleet/publications/blob/main/LAVA-NETWORK-SECURITY-ASSESSMENT.pdf",
    excerpt:
      "Lava is a Cosmos SDK chain for decentralized access to blockchain data. Nodefleet reviewed the Go implementation — 6 findings, no critical; the top issue is that conflict accountability hooks are effectively no-ops.",
    severity: [
      { level: "Critical", count: 0, summary: "No critical findings", color: SEV.critical },
      { level: "High", count: 1, summary: "Conflict jail/slash/reward hooks are no-ops", color: SEV.high },
      { level: "Medium", count: 4, summary: "Operator unstake, stake-movement validation, CU underflow, parameter validation", color: SEV.medium },
      { level: "Low", count: 1, summary: "Relay payment batch abort", color: SEV.low },
      { level: "Info", count: 0, summary: "No informational findings recorded", color: SEV.info },
    ],
    content: [
      { type: "callout", text: "Prepared for the Lava Network community. Repository under review: github.com/lavanet/lava (release v5.6.2). Engagement date: December 18, 2025." },
      { type: "heading", text: "Executive summary" },
      { type: "paragraph", text: "Lava Network is a Cosmos SDK chain for decentralized access to blockchain data. Its codebase combines standard SDK modules with Lava-specific modules for specifications, plans, subscriptions, projects, provider staking, dual staking, epoch storage, pairing, conflict handling, rewards, downtime tracking, protocol versioning, provider operation, and consumer relay routing — with on-chain accounting for compute-unit use, tracked rewards, unresponsive-provider complaints, and conflict votes." },
      { type: "paragraph", text: "The assessment identified 6 findings. No critical findings were recorded; one is High, four Medium, and one Low. The highest-risk issue is that the conflict accountability hooks used for jailing, slashing, and reward crediting are effectively no-ops in the audited code." },
      { type: "heading", text: "Key findings" },
      { type: "list", items: [
        "High — Conflict resolution executes a full vote lifecycle, but the pairing-keeper jail, slash, and reward hooks it calls return success without changing stake, jail state, or balances, materially weakening provider accountability.",
        "Medium — Provider operator keys can submit UnstakeProvider despite documentation stating that staking and unstaking are vault-only actions.",
        "Medium — Provider stake movement applies arithmetic to sdk.Coin values without validating positive amount, denom, source stake, or destination minimum.",
        "Medium — The project compute-unit limit calculation can underflow once usage exceeds the effective cap, reopening spending capacity instead of enforcing exhaustion.",
        "Medium — Governance-controlled epoch and payment-window parameters have placeholder validation, allowing values that can collapse memory windows or make payment and pairing timing unsafe.",
        "Low — Relay payment batches abort when any single relay remains rejected.",
      ] },
      { type: "heading", text: "Conclusion" },
      { type: "paragraph", text: "The architecture makes the main protocol loops understandable, but several module-local details cross economic and authority boundaries. The priority is restoring real consequences in the conflict-accountability hooks, then tightening stake-authority and parameter validation so provider accountability and compute-unit accounting behave as documented." },
      { type: "paragraph", text: PDF_CLOSING },
    ],
  },
  {
    slug: "passage3d-security-assessment",
    title: "Passage3D Blockchain: Security Assessment",
    date: "June 20, 2025",
    author: "Nodefleet Security",
    readingTime: "5 min read",
    tags: ["Security Assessment", "Cosmos SDK", "Metaverse", "Passage"],
    theme: passageTheme,
    pdfUrl:
      "https://github.com/nodefleet/publications/raw/main/PASSAGE3D-SECURITY-ASSESSMENT.pdf",
    pdfViewUrl:
      "https://github.com/nodefleet/publications/blob/main/PASSAGE3D-SECURITY-ASSESSMENT.pdf",
    excerpt:
      "Passage is a Cosmos SDK chain for a 3D metaverse. Nodefleet reviewed the Go implementation and its claim module — 7 findings, no critical or high issues, concentrated in application-specific state handling.",
    severity: [
      { level: "Critical", count: 0, summary: "No critical findings", color: SEV.critical },
      { level: "High", count: 0, summary: "No high-severity findings", color: SEV.high },
      { level: "Medium", count: 4, summary: "Multisig bypass, fee-grant migration, claim validation, init balances", color: SEV.medium },
      { level: "Low", count: 3, summary: "Unknown action strings, claim denom, hard-coded burn amounts", color: SEV.low },
      { level: "Info", count: 0, summary: "No informational findings recorded", color: SEV.info },
    ],
    content: [
      { type: "callout", text: "Prepared for the Passage3D community. Repository under review: github.com/envadiv/Passage3D. Engagement date: June 20, 2025." },
      { type: "heading", text: "Executive summary" },
      { type: "paragraph", text: "Passage is a Cosmos SDK application chain. The review covered its standard SDK module wiring (staking, bank, auth, distribution, slashing, governance, upgrade, IBC, CosmWasm) and its custom claim module, plus the v2.5.0 and v2.6.0 upgrade handlers that centralize claim-record replacement, airdrop funding, multisig migration, commission enforcement, and unclaimed-token burning." },
      { type: "paragraph", text: "The assessment identified 7 findings. No Critical or High findings were recorded; four are Medium and three are Low. The issues concern application-specific state handling rather than broad architecture failure." },
      { type: "heading", text: "Key findings" },
      { type: "list", items: [
        "Medium — The blocked-multisig ante decorator can be bypassed by placing a community-pool funding message before the restricted message in a multi-message transaction.",
        "Medium — Fee-grant migration copies grants to new accounts without revoking the old grants.",
        "Medium — Claim records are not schema-validated for action-array shape before indexed access, risking panics on malformed records.",
        "Medium — Claim module-account initialization can silently accept a positive but incorrect existing balance, making claim records appear valid while underfunded.",
        "Low — The claim transaction handler accepts any non-empty action string and maps unknown values to ActionInitialClaim.",
        "Low — The claim denomination parameter is not consistently honored across balance, export, and end-airdrop paths.",
        "Low — Hard-coded v2.6.0 burn amounts lack explicit preflight assertions and targeted tests.",
      ] },
      { type: "heading", text: "Conclusion" },
      { type: "paragraph", text: "Passage's custom surface is narrow and uses bank-keeper transfers rather than manual balance edits, which limits blast radius. The priority fixes are closing the multisig-ante bypass, revoking old fee grants on migration, and hardening claim-record validation and upgrade-time balance assumptions." },
      { type: "paragraph", text: PDF_CLOSING },
    ],
  },
  {
    slug: "story-protocol-v1-security-assessment",
    title: "Story Protocol V1: Security Assessment",
    date: "April 7, 2025",
    author: "Nodefleet Security",
    readingTime: "4 min read",
    tags: ["Security Assessment", "IP", "Solidity", "Story Protocol"],
    theme: storyTheme,
    pdfUrl:
      "https://github.com/nodefleet/publications/raw/main/STORY-PROTOCOL-V1-SECURITY-ASSESSMENT.pdf",
    pdfViewUrl:
      "https://github.com/nodefleet/publications/blob/main/STORY-PROTOCOL-V1-SECURITY-ASSESSMENT.pdf",
    excerpt:
      "Story Protocol is an EVM IP blockchain. Nodefleet reviewed the Proof-of-Creativity core contracts — 4 findings, no critical or high issues; the key issues are evidence-hash squatting and unproven cross-chain IP registration.",
    severity: [
      { level: "Critical", count: 0, summary: "No critical findings", color: SEV.critical },
      { level: "High", count: 0, summary: "No high-severity findings", color: SEV.high },
      { level: "Medium", count: 2, summary: "Evidence-hash squatting; cross-chain IP registration", color: SEV.medium },
      { level: "Low", count: 2, summary: "Stale reward-share watermark; reverting royalty transfer", color: SEV.low },
      { level: "Info", count: 0, summary: "No informational findings recorded", color: SEV.info },
    ],
    content: [
      { type: "callout", text: "Prepared for the Story Protocol community. Repository under review: github.com/storyprotocol/protocol-core-v1. Engagement date: April 7, 2025." },
      { type: "heading", text: "Executive summary" },
      { type: "paragraph", text: "Story Protocol's core implements EVM smart contracts for its Proof-of-Creativity protocol: ERC-6551 IP Accounts, IP asset and group registries, permission management, metadata modules, license templates and tokens, licensing flows, dispute handling with UMA arbitration, royalty modules and policies, IP royalty vaults, and group reward pools. Permissioned actions route through IP Account ownership and the AccessController, and upgrades are consistently gated through AccessManager." },
      { type: "paragraph", text: "The assessment identified 4 findings. No critical or high-severity findings were recorded; two are Medium and two are Low. The most important issues are in the dispute and cross-chain registration paths." },
      { type: "heading", text: "Key findings" },
      { type: "list", items: [
        "Medium — Evidence hashes are globally marked used when a dispute is raised but never released when it is cancelled, rejected, or resolved, creating an evidence-hash squatting path that can block legitimate dispute submission.",
        "Medium — Cross-chain IP registration permits token tuples on other chains without proving the token contract or token exists or that the registrant is authorized, producing IP identities that can be squatted or rendered unusable.",
        "Low — Removed group members can leave a stale reward-share high watermark that blocks later additions.",
        "Low — Royalty policy transfers revert instead of returning zero when no incremental amount is transferable.",
      ] },
      { type: "heading", text: "Conclusion" },
      { type: "paragraph", text: "The codebase is mature and modular, with strong access-control routing and broad test coverage. The priority fixes are releasing evidence-hash reservations when a dispute closes and requiring ownership or existence proof (or another authority layer) for cross-chain IP registration." },
      { type: "paragraph", text: PDF_CLOSING },
    ],
  },
  {
    slug: "pocket-network-shannon-security-assessment",
    title: "Pocket Network (Shannon): Security Assessment",
    date: "March 1, 2025",
    author: "Nodefleet Security",
    readingTime: "5 min read",
    tags: ["Security Assessment", "Cosmos SDK", "RPC", "Pocket Network"],
    theme: poktTheme,
    pdfUrl:
      "https://github.com/nodefleet/publications/raw/main/POCKET-NETWORK-SHANNON-SECURITY-ASSESSMENT.pdf",
    pdfViewUrl:
      "https://github.com/nodefleet/publications/blob/main/POCKET-NETWORK-SHANNON-SECURITY-ASSESSMENT.pdf",
    excerpt:
      "Pocket Network's poktroll implementation for the Shannon upgrade. Nodefleet reviewed the Cosmos SDK app — 8 findings, no critical, two High in Morse migration claims and supplier stake custody.",
    severity: [
      { level: "Critical", count: 0, summary: "No critical findings", color: SEV.critical },
      { level: "High", count: 2, summary: "Morse claim handler; owner-funded stake reductions", color: SEV.high },
      { level: "Medium", count: 4, summary: "Supplier/app unbonding lifecycle and proof concurrency", color: SEV.medium },
      { level: "Low", count: 2, summary: "Test keyring default; exact-fee rejection", color: SEV.low },
      { level: "Info", count: 0, summary: "No informational findings recorded", color: SEV.info },
    ],
    content: [
      { type: "callout", text: "Prepared for the Pocket Network community. Repository under review: github.com/pokt-network/poktroll (Shannon upgrade). Engagement date: March 1, 2025." },
      { type: "heading", text: "Executive summary" },
      { type: "paragraph", text: "Pocket Network's poktroll is a Cosmos SDK application with Pocket-specific modules for applications, gateways, suppliers, services, sessions, proofs, tokenomics, shared parameters, and Morse-to-Shannon migration. Module boundaries are clear, parameter updates route through governance authority checks, claim and proof state is indexed explicitly, and end-block ordering recognizes that proof validation must precede tokenomics settlement." },
      { type: "paragraph", text: "The assessment identified 8 findings. No critical findings were recorded; two are High, four Medium, and two Low. The highest-risk issues sit on paths that determine whether funds move and whether actors remain eligible for sessions." },
      { type: "heading", text: "Key findings" },
      { type: "list", items: [
        "High — Morse account claims return success without validating ownership or minting funds, leaving the migration claim flow incomplete.",
        "High — Supplier operators can receive owner-funded stake reductions, sending reduced stake to the signer rather than the custodial owner the non-custodial model describes.",
        "Medium — The supplier unbonding activity check is inverted.",
        "Medium — Proof validation performs concurrent SDK store and event operations by sharing context across goroutines inside an end blocker.",
        "Medium — Application activity remains true for unbonding applications without pending transfers.",
        "Medium — Unbonding end blockers skip session-end heights despite comments requiring session-end processing.",
        "Low — The daemon defaults to the test keyring backend.",
        "Low — Exact fee balances are rejected for service and proof fees.",
      ] },
      { type: "heading", text: "Conclusion" },
      { type: "paragraph", text: "No architectural redesign is required, but several module-local details cross important trust boundaries. Completing the Morse claim flow, sending reduced supplier stake to the custodial owner, and serializing proof-validation store access should be addressed before relying on the implementation for production migration, custodial supplier operation, or high-value relay settlement." },
      { type: "paragraph", text: PDF_CLOSING },
    ],
  },
  {
    slug: "dydx-chain-security-assessment",
    title: "dYdX Chain (protocol v7.0.5): Security Assessment",
    date: "December 2, 2024",
    author: "Nodefleet Security",
    readingTime: "5 min read",
    tags: ["Security Assessment", "Cosmos SDK", "Perp DEX", "dYdX"],
    theme: dydxTheme,
    pdfUrl:
      "https://github.com/nodefleet/publications/raw/main/DYDX-V4-SECURITY-ASSESSMENT.pdf",
    pdfViewUrl:
      "https://github.com/nodefleet/publications/blob/main/DYDX-V4-SECURITY-ASSESSMENT.pdf",
    excerpt:
      "dYdX Chain is a sovereign Cosmos SDK / CometBFT app for a decentralized perpetuals exchange. Nodefleet reviewed protocol release v7.0.5 — 5 findings, no critical, two Medium led by bridge destination handling.",
    severity: [
      { level: "Critical", count: 0, summary: "No critical findings", color: SEV.critical },
      { level: "High", count: 0, summary: "No high-severity findings", color: SEV.high },
      { level: "Medium", count: 2, summary: "Bridge destination padding/truncation; unbounded proposed operations", color: SEV.medium },
      { level: "Low", count: 3, summary: "Threshold validation, malformed-address parsing, log-parse panic", color: SEV.low },
      { level: "Info", count: 0, summary: "No informational findings recorded", color: SEV.info },
    ],
    content: [
      { type: "callout", text: "Prepared for the dYdX community. Repository under review: github.com/dydxprotocol/v4-chain at commit d9ce4d9074d302c3e8bec97bca35d63a55641ada (protocol/v7.0.5). Engagement date: December 2, 2024." },
      { type: "heading", text: "Executive summary" },
      { type: "paragraph", text: "dYdX Chain is a sovereign Cosmos SDK and CometBFT application for a decentralized perpetual-futures exchange. The architecture is materially more specialized than a stock Cosmos app: custom ABCI proposal preparation injects CLOB proposed operations, bridge acknowledgements, funding-premium votes, and market price updates into every block; the CLOB module keeps an in-memory orderbook; the bridge module recognizes Ethereum logs and completes accepted events after a delay; and release v7.0.5 includes the smart-account and permissioned-key system." },
      { type: "paragraph", text: "The assessment identified 5 findings. No critical findings were recorded; two are Medium and three are Low. The highest-impact issue concerns bridge destination handling." },
      { type: "heading", text: "Key findings" },
      { type: "list", items: [
        "Medium — Ethereum bridge destination bytes are normalized by padding values shorter than 20 bytes and truncating values longer than 32 bytes, which can turn malformed destinations into valid dYdX bech32 addresses and mint funds to an unintended account instead of rejecting the event.",
        "Medium — MsgProposedOperations has no dedicated count or byte cap before insertion; an oversized operations transaction can exceed the proposal budget and degrade block-proposal liveness.",
        "Low — Signer threshold validation permits non-majority thresholds for odd validator sets.",
        "Low — Bridge event parameter validation accepts malformed Ethereum address strings.",
        "Low — Bridge log parsing can panic on malformed Ethereum log data.",
      ] },
      { type: "heading", text: "Conclusion" },
      { type: "paragraph", text: "For a sovereign application chain, meaningful failures can arise from more than private-key compromise — an oversized proposer-injected transaction or a malformed bridge destination normalized into a different account can affect liveness and accounting even when consensus behaves correctly. The findings are concrete implementation fixes: reject malformed bridge destinations rather than normalizing them, bound the injected operations transaction, and harden bridge log parsing and threshold validation." },
      { type: "paragraph", text: PDF_CLOSING },
    ],
  },
  {
    slug: "wpokt-smart-contract-security-assessment",
    title: "wPOKT Smart Contract: Security Assessment",
    date: "December 2, 2024",
    author: "Nodefleet Security",
    readingTime: "4 min read",
    tags: ["Security Assessment", "Bridge", "ERC-20", "Pocket Network"],
    theme: wpoktTheme,
    pdfUrl:
      "https://github.com/nodefleet/publications/raw/main/WPOKT-SECURITY-ASSESSMENT.pdf",
    pdfViewUrl:
      "https://github.com/nodefleet/publications/blob/main/WPOKT-SECURITY-ASSESSMENT.pdf",
    excerpt:
      "wPOKT is the EVM bridge token for wrapped POKT. Nodefleet reviewed the contracts — 7 findings, no critical, two High: a burnFrom path that skips the redemption event and a 6-vs-18 decimal mismatch in the mint limiter.",
    severity: [
      { level: "Critical", count: 0, summary: "No critical findings", color: SEV.critical },
      { level: "High", count: 2, summary: "burnFrom skips redemption event; 6-vs-18 decimal mint mismatch", color: SEV.high },
      { level: "Medium", count: 3, summary: "Threshold, mint-payload binding, zero burn destination", color: SEV.medium },
      { level: "Low", count: 2, summary: "Cooldown validation; build reproducibility", color: SEV.low },
      { level: "Info", count: 0, summary: "No informational findings recorded", color: SEV.info },
    ],
    content: [
      { type: "callout", text: "Prepared for the Pocket Network community. Repository under review: github.com/pokt-network/wpokt. Engagement date: December 2, 2024." },
      { type: "heading", text: "Executive summary" },
      { type: "paragraph", text: "wPOKT is the ERC-20 bridge token representing wrapped POKT on EVM chains, with a mint controller that enforces mint limits and cooldowns and an addresses.json deployment record for operators. The review focused on mint and burn authorization, the validator-signed mint flow, burn redemption semantics, decimal handling, and build reproducibility." },
      { type: "paragraph", text: "The assessment identified 7 findings. No critical findings were recorded; two are High, three Medium, and two Low." },
      { type: "heading", text: "Key findings" },
      { type: "list", items: [
        "High — WrappedPocket blocks the inherited burn(uint256) but leaves burnFrom(address,uint256) available through ERC20Burnable, letting approved spenders destroy balances without emitting the bridge redemption event.",
        "High — Mint limits and cooldown rates use 18-decimal (ether) units while wPOKT declares 6 decimals, weakening the mint limiter by a factor of 10^12.",
        "Medium — The signer-threshold setter permits non-majority thresholds for odd validator counts, allowing minority mint authorization.",
        "Medium — The signed mint payload includes only recipient, amount, and nonce, leaving source-chain event identity and expiry outside the signed message.",
        "Medium — The burn path accepts an Ethereum address as the Pocket destination and permits the zero address, making redemption events ambiguous or unrecoverable.",
        "Low — setMintCooldown validates the new limit against the old cooldown rate rather than the requested new rate.",
        "Low — Release build inputs are not fully pinned or consistently configured.",
      ] },
      { type: "heading", text: "Conclusion" },
      { type: "paragraph", text: "The contract is straightforward, but two High-severity issues undermine its core guarantees: every supply reduction should emit the redemption event the bridge relies on, and the mint limiter must use the token's declared 6 decimals to mean what it says. Binding mint signatures to source-chain identity and expiry and enforcing a true majority threshold further harden the bridge." },
      { type: "paragraph", text: PDF_CLOSING },
    ],
  },
];

export const getPostBySlug = (slug) => posts.find((p) => p.slug === slug);
